import { useQuery } from '@tanstack/react-query';
import { RepeatControl, ShuffleControl } from './ModeControls';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

import { Avatar } from '../../components/ui/Avatar';
import { Explicit } from '../../components/ui/Explicit';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { Lights } from './Lights';
import { usePeaks } from './peaks';
import { FollowButton } from '../profile/FollowButton';
import type { Profile } from '../profile/types';
import type { Credit } from '../tracks/CreditsEditor';
import { Verified } from '../../components/ui/Verified';

/**
 * The track, filling the screen.
 *
 * ## It reads the player, it does not contain it
 *
 * The single `<audio>` element lives in the dock, outside the router outlet, and
 * everything about playback surviving navigation depends on it staying there
 * (see `AppShell`). So this is a sibling of the dock rather than a wrapper
 * around it: it subscribes to the same store, draws a bigger version of the
 * same state, and its buttons call the same actions. Opening and closing it
 * cannot interrupt a note, because nothing about the audio moves.
 *
 * ## The real fullscreen, not a big div
 *
 * `requestFullscreen` on the overlay, so it covers the browser chrome, the
 * taskbar and the notch - which is the entire point of the feature. Escape is
 * then handled by the browser rather than by us, and `fullscreenchange` is what
 * closes this: the user has three ways out (Escape, F11, the browser's own exit
 * affordance) and only one of them would ever have reached a keydown listener.
 *
 * It degrades rather than refuses. iOS Safari does not grant fullscreen to
 * anything but a video element, and there the promise rejects and this stays a
 * fixed overlay - covering the page, not the browser. Still the useful part.
 *
 * ## There is more below the fold
 *
 * Filling a screen with one image and three buttons is a screensaver. What
 * somebody looking at a track they are enjoying actually wants next is to keep
 * it, and then to find out who made it - so the panel scrolls, and underneath
 * the artwork are the credits and the artist.
 *
 * Both come from queries the app already runs: `['track', id]` is what the
 * track page uses and `['profile', username]` is what the hover cards and the
 * artist drawer use. Opening this on a track whose page you have already
 * visited costs no request at all, and anything it does fetch is warm for the
 * page you open next.
 *
 * ## The background is the artwork
 *
 * Blurred, scaled up and dimmed, rather than a colour sampled from it. Sampling
 * means drawing the image to a canvas and reading pixels back, which is a
 * tainted-canvas error the day the artwork host's CORS headers change - a
 * failure that would appear as a black screen long after anybody remembered
 * why. A blurred copy of the same `<img>` cannot fail that way and gets most of
 * the effect.
 */

interface Screen {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * Its own store because the two halves are far apart in the tree: the button is
 * inside the dock and the screen is a sibling of it, and threading a callback
 * up through `PlayerBar` would couple the bar to a layout above it.
 */
export const useNowPlayingScreen = create<Screen>()((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

export function NowPlayingScreen() {
  const open = useNowPlayingScreen((s) => s.open);
  const setOpen = useNowPlayingScreen((s) => s.setOpen);

  const track = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const toggle = usePlayer((s) => s.toggle);
  const next = usePlayer((s) => s.next);
  const previous = usePlayer((s) => s.previous);
  const seek = usePlayer((s) => s.seek);
  const held = usePlayer((s) => s.locked && s.source === 'jam');

  const root = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [native, setNative] = useState(false);

  /*
   * The artwork's own bloom, driven straight from the peaks.
   *
   * A ref because the visualiser writes to it sixty times a second. Pushing the
   * level through state would re-render this whole screen every frame to move
   * one blurred copy of an image.
   */
  const glow = useRef<HTMLDivElement>(null);

  // Fetched once per track, and shared with the scrubber's waveform - the same
  // array from the same URL.
  const peaks = usePeaks(open ? (track?.waveform_url ?? null) : null);

  const id = track?.id ?? null;
  const username = track?.owner?.username ?? null;

  /*
   * The same keys the track page and the hover cards use, so this shares their
   * cache in both directions rather than fetching a second copy of everything.
   */
  const detail = useQuery({
    queryKey: ['track', String(id)],
    queryFn: () => api.get<{ description: string | null; credits: Credit[] }>(`/tracks/${id}`),
    enabled: open && id !== null,
    staleTime: 60_000,
  });

  const artist = useQuery({
    queryKey: ['profile', username],
    queryFn: () => api.get<Profile>(`/users/${username}`),
    enabled: open && username !== null,
    staleTime: 60_000,
  });

  /* Ask for real fullscreen once the overlay is actually in the document. */
  useEffect(() => {
    if (!open) return;

    const element = root.current;

    if (!element) return;

    element
      .requestFullscreen?.()
      .then(() => setNative(true))
      // Refused - iOS, or a permissions policy. The overlay stands on its own.
      .catch(() => setNative(false));

    return () => {
      setNative(false);

      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, [open]);

  /*
   * The browser owns the exits, so the browser tells us when one was taken.
   * Escape, F11 and the floating "exit full screen" control all arrive here and
   * nowhere else.
   */
  useEffect(() => {
    if (!open) return;

    const changed = () => {
      if (native && !document.fullscreenElement) setOpen(false);
    };

    document.addEventListener('fullscreenchange', changed);

    return () => document.removeEventListener('fullscreenchange', changed);
  }, [open, native, setOpen]);

  /* Escape still closes when fullscreen was refused and nothing else would. */
  useEffect(() => {
    if (!open || native) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [open, native, setOpen]);

  /*
   * Closed when there is nothing to fill the screen with.
   *
   * The queue running out while this is open would otherwise leave somebody
   * looking at a blank rectangle with no obvious way back.
   */
  useEffect(() => {
    if (open && track === null) setOpen(false);
  }, [open, track, setOpen]);

  if (!open || !track) return null;

  const total = duration || (track.duration_ms ?? 0) / 1000;
  const progress = total > 0 ? Math.min(1, position / total) : 0;
  const art = track.artwork ?? track.artwork_small ?? null;

  return (
    <div
      ref={root}
      data-now-playing
      className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-app"
    >
      {/* Fixed, not absolute: the panel scrolls now, and a backdrop that
          scrolled with it would slide a blurred sleeve up the screen. */}
      {art && (
        <img
          src={art}
          alt=""
          aria-hidden
          /*
           * Scaled well past the edges so the blur has material to work with -
           * blurring to the edge of an image that stops at the viewport leaves
           * a visible soft border where it runs out.
           */
          className="pointer-events-none fixed inset-0 h-full w-full scale-125 object-cover opacity-30 blur-3xl"
        />
      )}
      <div aria-hidden className="pointer-events-none fixed inset-0 bg-app/70" />

      {/*
        The room lights, on the beat.

        Above the veil and below everything else, which is the whole idea: it is
        the room rather than the record. See `Lights` for why the beat is a
        measurement rather than an animation running near some music.
      */}
      <Lights
        peaks={peaks}
        duration={total}
        playing={playing}
        position={position}
        glow={glow}
      />

      <section className="relative flex min-h-full flex-col items-center justify-center gap-8 px-6 py-10">

      {/*
        Bottom right, where the button that opened this one lives.

        It was a cross in the top corner, which was wrong twice. A cross means
        "close", and this collapses back to a page that was never gone - the
        same relationship the dock's expand icon describes, so it gets that icon
        reversed. And in the desktop shell the top right belongs to the
        operating system: the caption buttons are drawn above the page there,
        so an exit button sat directly beneath a quit button. Down here no
        platform has anything, and the inset that dodged them is gone with it.
      */}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Exit full screen"
        title="Exit full screen"
        data-now-playing-close
        className="fixed bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
      >
        {/* The dock's expand glyph, turned inward. */}
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2 6h4V2M14 6h-4V2M2 10h4v4M14 10h-4v4" />
        </svg>
      </button>

      {/* Sized against the shorter axis, so a wide monitor does not push the
          controls off the bottom and a tall one does not leave the art tiny. */}
      <div className="relative aspect-square w-[min(58vh,40rem,86vw)] shrink-0">
        {/*
          Light coming off the cover, at the volume of the thing playing.

          The artwork blurred behind itself rather than a coloured halo, so the
          bloom is the record's own palette without anybody having to sample it.
          `Visualiser` sets the opacity and the scale each frame; the values
          here are only what it looks like before the first one lands.
        */}
        {art && (
          <div
            ref={glow}
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[var(--radius-card)] blur-2xl transition-none will-change-transform"
            style={{
              backgroundImage: `url(${art})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.18,
              transform: 'scale(1.02)',
            }}
          />
        )}

        {art ? (
          <img
            src={art}
            alt=""
            className="relative h-full w-full rounded-[var(--radius-card)] object-cover shadow-2xl"
          />
        ) : (
          <div
            aria-hidden
            className="relative h-full w-full rounded-[var(--radius-card)] shadow-2xl"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg,var(--color-blank) 0 22px,var(--color-blank-alt) 22px 44px)',
            }}
          />
        )}
      </div>

      <div className="relative flex w-[min(42rem,90vw)] flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="flex items-center justify-center gap-2 text-pretty text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            {track.explicit && <Explicit />}
            {track.title}
          </h1>
          {track.owner && (
            <p className="text-lg text-soft">{track.owner.display_name}</p>
          )}
        </div>

        {/*
          A plain range input rather than the dock's waveform.

          The waveform is drawn from a peaks file at a fixed height and is the
          right instrument at 36 pixels in a strip. Stretched across a hundred
          centimetres of screen it is a smear, and what somebody in this view
          actually wants is the coarse "how far through am I" that a bar says
          better.
        */}
        <div className="flex items-center gap-3">
          <span className="meta shrink-0 tabular-nums text-faint">{clock(position)}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={progress}
            onChange={(event) => seek(Number(event.target.value) * total)}
            aria-label="Seek"
            data-now-playing-seek
            className="h-1 min-w-0 flex-1 accent-[var(--color-signal)]"
          />
          <span className="meta shrink-0 tabular-nums text-faint">{clock(total)}</span>
        </div>

        {/*
          One row of five, symmetrical about the play button.

          Keeping a track and filing it are things you do *while* it is playing,
          so they belong with the transport rather than in a second row of
          differently-shaped buttons underneath it. Both sit at the transport's
          size, in its shape, with its hover wash - five controls that read as
          one row instead of three plus two.

          The heart shows no count here. A number that changes width on the
          leftmost control drags the play button off the centre of the screen,
          which is the same drift that had to be fixed in the dock - and this
          view is the one place the artwork makes the centre obvious.
        */}
        <div className="flex items-center justify-center gap-3 sm:gap-6">
          <FavoriteButton
            trackId={track.id}
            favorited={track.favorited}
            likeCount={track.like_count}
            size="lg"
          />

          <ShuffleControl size="lg" />

          <Round label="Previous" onClick={previous} disabled={held}>
            <path d="M13 4v8L7 8l6-4zM5 4h1.5v8H5z" />
          </Round>

          <button
            type="button"
            onClick={toggle}
            disabled={held}
            title={held ? 'Only the host controls playback in this jam' : undefined}
            aria-label={playing ? 'Pause' : 'Play'}
            data-now-playing-toggle
            className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
          >
            <svg viewBox="0 0 16 16" className="h-6 w-6" fill="currentColor" aria-hidden>
              {playing ? <path d="M5 3h2.5v10H5zM8.5 3H11v10H8.5z" /> : <path d="M5 3l7 5-7 5z" />}
            </svg>
          </button>

          <Round label="Next" onClick={next} disabled={held}>
            <path d="M3 4l6 4-6 4V4zM9.5 4H11v8H9.5z" />
          </Round>

          <RepeatControl size="lg" />

          <AddToPlaylist trackId={track.id} title={track.title} size="lg" />
        </div>

        {/*
          The one thing that leaves.

          On its own line because it is the only control here that is not about
          the track playing - it is about going somewhere else - and a link
          shaped like a button beside five round ones was reading as a sixth
          transport control.
        */}
        <div className="flex justify-center">
          <button
            type="button"
            data-now-playing-open-track
            onClick={() => {
              // Closed first: navigating underneath a full-screen panel leaves
              // somebody looking at the same artwork wondering what happened.
              setOpen(false);
              navigate(`/tracks/${track.id}`);
            }}
            className="rounded-[var(--radius-pill)] border border-line px-4 py-2.5 text-sm text-soft transition-colors hover:border-line-strong hover:text-ink"
          >
            Track page
          </button>
        </div>
      </div>

      {/*
        Says there is something below, because nothing else does.

        A panel that fills the screen exactly gives no hint that it scrolls, and
        an affordance nobody knows about is the same as not building it. Hidden
        once there is nothing left to point at.
      */}
      {(artist.data || detail.data) && (
        <span aria-hidden className="relative -mb-4 mt-2 animate-pulse text-ghost">
          <svg viewBox="0 0 16 16" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6l4 4 4-4" />
          </svg>
        </span>
      )}
      </section>

      {(artist.data || detail.data) && (
        <section className="relative mx-auto flex w-[min(52rem,90vw)] flex-col gap-10 px-1 pb-28">
          {artist.data && (
            <div className="flex flex-col gap-4">
              <h2 className="meta-caps text-faint">About the artist</h2>

              <div className="flex flex-wrap items-center gap-4">
                <Avatar src={artist.data.avatar?.medium ?? null} name={artist.data.display_name} size={72} />

                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate(`/${artist.data!.username}`);
                    }}
                    className="text-left text-xl font-bold tracking-tight text-ink hover:underline"
                  >
                    {artist.data.display_name}
                    {artist.data?.verified && <Verified />}
                  </button>
                  <span className="font-mono text-[11px] text-faint">
                    {artist.data.follower_count.toLocaleString()} followers ·{' '}
                    {artist.data.track_count.toLocaleString()} tracks
                  </span>
                </div>

                <FollowButton profile={artist.data} />
              </div>

              {artist.data.bio && (
                <p className="max-w-2xl text-pretty text-sm leading-relaxed text-soft">
                  {artist.data.bio}
                </p>
              )}
            </div>
          )}

          {detail.data?.description && (
            <div className="flex flex-col gap-3">
              <h2 className="meta-caps text-faint">About this track</h2>
              <p className="max-w-2xl text-pretty text-sm leading-relaxed text-soft">
                {detail.data.description}
              </p>
            </div>
          )}

          {detail.data && <Credits credits={detail.data.credits} />}
        </section>
      )}
    </div>
  );
}

/**
 * Who made it, grouped by what they did.
 *
 * Pending and declined credits are left out: a credit somebody has not accepted
 * is a claim, and this is not the screen to litigate one on.
 */
function Credits({ credits }: { credits: Credit[] }) {
  const shown = credits.filter((c) => c.status === undefined || c.status === 'accepted');

  if (shown.length === 0) return null;

  const groups = new Map<string, string[]>();

  for (const credit of shown) {
    groups.set(credit.role, [...(groups.get(credit.role) ?? []), credit.name]);
  }

  return (
    <div className="flex flex-col gap-3">
      <h2 className="meta-caps text-faint">Credits</h2>

      <dl className="flex flex-col gap-2">
        {[...groups].map(([role, names]) => (
          <div key={role} className="flex flex-wrap items-baseline gap-x-3">
            <dt className="min-w-[7rem] font-mono text-[11px] capitalize text-faint">{role}</dt>
            <dd className="text-sm text-ink">{names.join(', ')}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Round({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Only the host controls playback in this jam' : undefined}
      aria-label={label}
      className="flex h-12 w-12 items-center justify-center rounded-full text-soft transition-colors hover:bg-raised hover:text-ink disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <svg viewBox="0 0 16 16" className="h-6 w-6" fill="currentColor" aria-hidden>
        {children}
      </svg>
    </button>
  );
}

/** `3:41`. Same shape as the dock's, kept local so neither has to export it. */
function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);

  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}
